import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, ListChecks } from "lucide-react";
import { useAuth } from "../lib/auth";

// A dashboard checklist that disappears only when every step is complete.
// Completion is computed from actual saved data (never a hardcoded flag).
export default function SetupChecklist() {
  const { user } = useAuth();

  const items = useMemo(() => {
    if (!user) return [];
    const campaigns = user.campaigns || {};
    const hasCampaign =
      !!user.onboarding_objective ||
      Object.values(campaigns).some((c) => Array.isArray(c?.creatives) && c.creatives.some(Boolean));
    return [
      { key: "business", label: "Complete business profile", to: "/dashboard/profile", done: !!(user.name && user.target_city && user.account_type) },
      { key: "brand", label: "Complete Brand Kit", to: "/dashboard/profile", done: !!(user.brand_logo || user.headshot || user.business_phone) },
      { key: "meta", label: "Connect Meta account", to: "/dashboard/settings", done: !!user.integrations?.meta },
      { key: "campaign", label: "Create first campaign", to: "/dashboard/creative", done: hasCampaign },
    ];
  }, [user]);

  if (!user || !user.onboarding_completed) return null;
  const doneCount = items.filter((i) => i.done).length;
  if (doneCount === items.length) return null; // nothing left to do

  return (
    <div className="mb-6 bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 shadow-sm" data-testid="setup-checklist">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#1D6AFF]/10 border border-[#1D6AFF]/20 flex items-center justify-center">
            <ListChecks className="w-4 h-4 text-[#1D6AFF]" />
          </div>
          <div>
            <div className="font-semibold text-slate-900 text-sm">Finish setting up Newground</div>
            <div className="text-xs text-slate-500">{doneCount} of {items.length} complete</div>
          </div>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        {items.map((it) => (
          <Link key={it.key} to={it.to} data-testid={`checklist-item-${it.key}`}
            className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 border transition-all ${it.done ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200 hover:border-[#1D6AFF]/40 hover:bg-white"}`}>
            {it.done ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> : <Circle className="w-4 h-4 text-slate-300 shrink-0" />}
            <span className={`text-sm ${it.done ? "text-emerald-700 line-through" : "text-slate-700 font-medium"}`}>{it.label}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}
